// src/pages/GroupsPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  Snackbar,
  Alert,
  Menu,
  TablePagination,
  Tooltip,
} from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { request } from '../utils/request';
import { ColorModeContext } from '../index';
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

export default function GroupsPage() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];
  const hasPermission = (perm) => isSuperAdmin || userPermissions.includes(perm);

  // Exact permission mapping matching your requirements
  const canCreate = hasPermission('Browser Control-Groups-GroupsWrite');
  const canEdit = hasPermission('Browser Control-Groups-GroupsUpdate');
  const canDelete = hasPermission('Browser Control-Groups-GroupsDelete');

  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [urls, setUrls] = useState([]);

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    users: [],
    department: '',
    location: '',
    description: '',
    enabled_policies: [],
    enabled_urls: [],
  });

  const [selectedIds, setSelectedIds] = useState([]);

  const [usersMenuOpen, setUsersMenuOpen] = useState(false);
  const [usersBackup, setUsersBackup] = useState([]);

  const [policiesMenuOpen, setPoliciesMenuOpen] = useState(false);
  const [policiesBackup, setPoliciesBackup] = useState([]);

  const [urlsMenuOpen, setUrlsMenuOpen] = useState(false);
  const [urlsBackup, setUrlsBackup] = useState([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const onMenuEdit = (row) => {
    if (!canEdit) return;
    handleMenuClose();
    handleOpen(row);
  };

  const onMenuDelete = (row) => {
    if (!canDelete) return;
    handleMenuClose();
    handleDeleteClick(row);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [groups.length]);

  const fetchAll = async () => {
    try {
      const [grpData, usrData, polData, urlData] = await Promise.all([
        request('groups', { method: 'get' }),
        request('user', { method: 'get' }),
        request('policies', { method: 'get' }),
        request('urls', { method: 'get' }),
      ]);
      setGroups(Array.isArray(grpData) ? grpData : []);
      setUsers(Array.isArray(usrData) ? usrData : []);
      setPolicies(Array.isArray(polData) ? polData : []);
      setUrls(Array.isArray(urlData) ? urlData : []);
    } catch (err) {
      console.error('Failed to fetch data for GroupsPage:', err);
      setGroups([]);
      setUsers([]);
      setPolicies([]);
      setUrls([]);
      showSnack('Failed to load data.', 'error');
    }
  };

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const getNames = (ids, list, key = 'username') =>
    ids?.length
      ? ids
        .map((id) => list.find((item) => item._id === id)?.[key] || '—')
        .join(', ')
      : '—';

  const paginatedGroups = groups.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleChangePage = (event, nextPage) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpen = (grp) => {
    if (grp) {
      if (!canEdit) return;
      setFormData({
        id: grp._id,
        name: grp.name || '',
        users: grp.users || [],
        department: grp.department || '',
        location: grp.location || '',
        description: grp.description || '',
        enabled_policies: grp.enabled_policies || [],
        enabled_urls: grp.enabled_urls || [],
      });
      setIsEditing(true);
    } else {
      if (!canCreate) return;
      setFormData({
        id: null,
        name: '',
        users: [],
        department: '',
        location: '',
        description: '',
        enabled_policies: [],
        enabled_urls: [],
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!isEditing && !canCreate) return;
    if (isEditing && !canEdit) return;

    if (!formData.name.trim()) {
      showSnack('Group Name is required.', 'error');
      return;
    }
    if (formData.users.length === 0) {
      showSnack('Please select at least one user.', 'error');
      return;
    }
    if (!formData.department.trim()) {
      showSnack('Department is required.', 'error');
      return;
    }
    if (formData.description && formData.description.length > 500) {
      showSnack('Description cannot exceed 500 characters.', 'error');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        users: formData.users,
        department: formData.department,
        location: formData.location,
        description: formData.description,
        enabled_policies: formData.enabled_policies,
        enabled_urls: formData.enabled_urls,
      };
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: payload }
        : { method: 'post', body: payload };
      await request('groups', opts);
      await fetchAll();
      handleClose();
      showSnack(isEditing ? 'Group updated successfully.' : 'Group created successfully.', 'success');
    } catch (err) {
      console.error('Error saving group:', err);
      showSnack('Failed to save group.', 'error');
    }
  };

  const handleDeleteClick = (group) => {
    if (!canDelete) return;
    setDeletingGroup(group);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingGroup(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingGroup || !deletingGroup._id) return;
    setDeleting(true);
    try {
      await request('groups', { method: 'delete', params: deletingGroup._id });
      await fetchAll();
      showSnack(`Group "${deletingGroup.name}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      console.error('Failed to delete group:', err);
      showSnack('Failed to delete group.', 'error');
      setDeleting(false);
    }
  };

  const handleOpenBulkDelete = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => request('groups', { method: 'delete', params: id }))
      );
      showSnack(`${selectedIds.length} group(s) deleted.`, 'success');
      setSelectedIds([]);
      await fetchAll();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showSnack('Failed to delete selected groups.', 'error');
      setBulkDeleting(false);
    }
  };

  const computeAvailableUsers = () => {
    const assignedUserIds = new Set();
    groups.forEach((g) => {
      if (formData.id && g._id === formData.id) return;
      if (Array.isArray(g.users)) {
        g.users.forEach((uid) => {
          if (uid) assignedUserIds.add(uid.toString());
        });
      }
    });
    return users.filter((u) => !assignedUserIds.has((u._id || '').toString()));
  };

  const availableUsers = computeAvailableUsers();

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = groups.length > 0 && selectedIds.length === groups.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(groups.map((g) => g._id));
    }
  };

  const handleEditSelected = () => {
    if (!canEdit) return;
    if (selectedIds.length !== 1) return;
    const grp = groups.find((g) => g._id === selectedIds[0]);
    if (grp) {
      handleOpen(grp);
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    handleOpenBulkDelete();
  };

  // Replaced inline Dropdown footer style with Global vars mapping
  const dropdownFooterStyle = {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 2,
    p: 1.5,
    px: 2,
    pr: 3, // Prevent scrollbar overlap
    borderTop: '1px solid var(--hairline)',
    bgcolor: 'var(--canvas)',
    zIndex: 2,
  };

  const customDropdownMenuProps = {
    PaperProps: {
      className: "global-card",
      sx: {
        maxHeight: 350,
        minWidth: 350, // Force a clean, wide dropdown
        overflowY: 'auto', // Force scrolling on Paper for custom scrollbar
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(11, 37, 69, 0.15)',
        border: '1px solid var(--hairline)',
        '& .MuiList-root': {
          p: 0,
        },
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.3)' },
      }
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      fontFamily: 'Calibri',
      fontSize: '16px',
      color: 'var(--vault-navy)',
      minHeight: '48px',
      '& fieldset': {
        borderColor: 'var(--hairline)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'var(--hairline)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--edge-blue)',
        borderWidth: '2px',
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px var(--blue-tint)',
      }
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Calibri',
      fontSize: '16px',
      color: 'var(--slate)',
      '&.Mui-focused': {
        color: 'var(--edge-blue)',
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '12.5px 14px',
    }
  };

  const renderUsers = (ids, list) => {
    if (!ids || ids.length === 0) return <Typography component="span" sx={{ color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '14px' }}>—</Typography>;
    const displayIds = ids.slice(0, 3);
    const extraCount = ids.length - 3;
    const colors = ['#E3F2FD', '#F3E5F5', '#E8F5E9', '#FFF3E0'];
    const textColors = ['#1976D2', '#7B1FA2', '#388E3C', '#F57C00'];
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {displayIds.map((id, index) => {
          const item = list.find(x => x._id === id);
          const text = item ? item.username : id;
          const initial = typeof text === 'string' && text.length > 0 ? text.charAt(0).toUpperCase() : '?';
          const cIdx = index % colors.length;
          return (
            <Box
              key={id}
              title={text}
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                bgcolor: colors[cIdx],
                color: textColors[cIdx],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              {initial}
            </Box>
          );
        })}
        {extraCount > 0 && (
          <Tooltip 
            title={ids.map(id => list.find(x => x._id === id)?.username || id).join(', ')} 
            arrow 
            placement="top"
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                bgcolor: 'var(--mist)',
                color: 'var(--slate)',
                border: '1px solid var(--hairline)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              +{extraCount}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderChips = (ids, list, key = 'name', type = 'policy') => {
    if (!ids || ids.length === 0) return <Typography component="span" sx={{ color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '14px' }}>—</Typography>;
    
    if (type === 'url') {
      const count = ids.length;
      return (
        <Tooltip
          title={ids.map(id => list.find(x => x._id === id)?.[key] || id).join(', ')}
          arrow
          placement="top"
        >
          <Box
            sx={{
              display: 'inline-block',
              bgcolor: 'var(--mist)',
              color: 'var(--edge-blue)',
              borderRadius: '12px',
              px: 1.5,
              py: 0.25,
              fontFamily: 'Calibri',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              cursor: 'pointer'
            }}
          >
            {count} {count === 1 ? 'URL' : 'URLs'}
          </Box>
        </Tooltip>
      );
    }
    
    const displayIds = ids.slice(0, 2);
    const extraCount = ids.length - 2;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {displayIds.map(id => {
          const item = list.find(x => x._id === id);
          const text = item ? item[key] : id;
          return (
            <Box
              key={id}
              sx={{
                bgcolor: 'var(--blue-tint)',
                color: 'var(--edge-blue)',
                borderRadius: '4px',
                px: 1,
                py: 0.25,
                fontFamily: 'Calibri',
                fontSize: '12px',
                whiteSpace: 'nowrap'
              }}
            >
              {text}
            </Box>
          );
        })}
        {extraCount > 0 && (
          <Tooltip 
            title={ids.slice(2).map(id => list.find(x => x._id === id)?.[key] || id).join(', ')} 
            arrow 
            placement="top"
          >
            <Box
              sx={{
                bgcolor: 'var(--mist)',
                color: 'var(--vault-navy)',
                borderRadius: '4px',
                border: '1px solid var(--hairline)',
                px: 1,
                py: 0.25,
                fontFamily: 'Calibri',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              +{extraCount}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box p={3} sx={{ minHeight: '100vh', bgcolor: 'var(--mist)' }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link
          href="/home"
          underline="hover"
          sx={{
            fontFamily: 'Calibri',
            fontSize: '16px',
            color: 'var(--edge-blue)',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          Dashboard
        </Link>
        <Typography sx={{ fontFamily: 'Calibri', fontSize: '16px', fontWeight: 700, color: 'var(--vault-navy)' }}>
          Groups
        </Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" mb={3}>
        {canCreate && (
          <Button
            variant="contained"
            onClick={() => handleOpen()}
            sx={{
              bgcolor: 'var(--edge-blue)',
              color: '#FFF',
              fontFamily: 'Arial',
              fontWeight: 'bold',
              fontSize: '16px',
              borderRadius: '6px',
              height: '40px',
              px: 3,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'var(--edge-blue)',
                boxShadow: '0 0 0 3px var(--blue-tint)',
              },
              '&:focus-visible': {
                outline: '2px solid var(--edge-blue)',
                outlineOffset: '2px',
              }
            }}
          >
            Add Group
          </Button>
        )}
      </Stack>

      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography sx={{ fontFamily: 'Calibri', fontSize: '16px', color: 'var(--vault-navy)', fontWeight: 700, mr: 1 }}>
            {selectedIds.length} selected
          </Typography>

          {canEdit && (
            <Button
              size="small"
              onClick={handleEditSelected}
              disabled={selectedIds.length !== 1}
              sx={{
                bgcolor: '#FFF',
                color: 'var(--vault-navy)',
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '6px',
                height: '32px',
                border: '1px solid var(--hairline)',
                textTransform: 'none',
                '&:hover:not(:disabled)': {
                  borderColor: 'var(--edge-blue)',
                  boxShadow: '0 0 0 3px var(--blue-tint)',
                  bgcolor: '#FFF',
                }
              }}
            >
              Edit
            </Button>
          )}

          {canDelete && (
            <Button
              size="small"
              onClick={handleDeleteSelected}
              sx={{
                bgcolor: '#FFF',
                color: 'var(--critical)',
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '6px',
                height: '32px',
                border: '1px solid var(--critical)',
                textTransform: 'none',
                '&:hover:not(:disabled)': {
                  bgcolor: 'rgba(214, 50, 58, 0.04)',
                }
              }}
            >
              Delete
            </Button>
          )}

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{
              color: 'var(--edge-blue)',
              fontFamily: 'Arial',
              fontWeight: 'bold',
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                textDecoration: 'underline',
                bgcolor: 'transparent'
              }
            }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          bgcolor: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          mb: 3,
          boxShadow: 'none'
        }}
      >
        <Box
          sx={{
            overflowX: 'auto',
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--slate) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'var(--slate)', borderRadius: '10px' },
          }}
        >
          <Box
            component="table"
            sx={{ 
              minWidth: 1000, 
              width: '100%', 
              borderCollapse: 'collapse',
              textAlign: 'left'
            }}
          >
            <Box component="thead" sx={{ bgcolor: '#FFF', borderBottom: '1px solid var(--hairline)' }}>
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    p: '8px 16px',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'select all groups' }}
                    sx={{
                      p: '4px',
                      color: 'var(--slate)',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: 'var(--edge-blue)',
                      },
                      '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                    }}
                  />
                </Box>

                {[
                  { label: '#', width: '5%' },
                  { label: 'Name', width: '15%' },
                  { label: 'Users', width: '20%' },
                  { label: 'Department', width: '10%' },
                  { label: 'Location', width: '10%' },
                  { label: 'Description', width: '10%' },
                  { label: 'Policies', width: '15%' },
                  { label: 'URLs', width: '10%' },
                  ...(canEdit || canDelete ? [{ label: 'Actions', width: '80px' }] : [])
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    textAlign={col.label === 'Actions' ? 'center' : 'left'}
                    sx={{
                      p: '8px 16px',
                      width: col.width,
                      color: 'var(--vault-navy)',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '13px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box component="tbody">
              {paginatedGroups.map((g, i) => (
                <Box 
                  component="tr" 
                  key={g._id}
                  sx={{
                    bgcolor: '#FFF',
                    borderBottom: '1px solid var(--hairline)',
                    '&:hover': { bgcolor: 'var(--mist)' },
                  }}
                >
                  <Box component="td" sx={{ p: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <Checkbox
                      checked={selectedIds.includes(g._id)}
                      onChange={() => toggleSelect(g._id)}
                      inputProps={{ 'aria-label': `select group ${g.name}` }}
                      sx={{
                        p: '4px',
                        color: 'var(--slate)',
                        '&.Mui-checked': { color: 'var(--edge-blue)' },
                        '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                      }}
                    />
                  </Box>

                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                    {page * rowsPerPage + i + 1}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Arial, sans-serif', fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)' }}>
                    {g.name}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                    {renderUsers(g.users, users)}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                    {g.department || <Typography component="span" sx={{ color: 'var(--slate)' }}>—</Typography>}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                    {g.location || <Typography component="span" sx={{ color: 'var(--slate)' }}>—</Typography>}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {g.description || <Typography component="span" sx={{ color: 'var(--slate)' }}>—</Typography>}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                    {renderChips(g.enabled_policies, policies, 'name', 'policy')}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                    {renderChips(g.enabled_urls, urls, 'category_Name', 'url')}
                  </Box>

                  {(canEdit || canDelete) && (
                    <Box component="td" sx={{ p: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <IconButton
                        size="small"
                        aria-label={`more-actions-${g._id}`}
                        onClick={(e) => handleMenuOpen(e, g._id)}
                        sx={{
                          width: '32px',
                          height: '32px',
                          color: 'var(--slate)',
                          '&:hover': {
                            bgcolor: 'var(--mist)',
                            color: 'var(--vault-navy)',
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      <Menu
                        id={`row-menu-${g._id}`}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuRowId === g._id}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ 'aria-labelledby': `more-actions-${g._id}` }}
                        PaperProps={{ 
                          sx: { 
                            bgcolor: 'var(--canvas)',
                            border: '1px solid var(--hairline)',
                            boxShadow: '0 4px 12px rgba(11, 37, 69, 0.08), 0 1px 2px rgba(11, 37, 69, 0.04)',
                            borderRadius: '12px',
                            mt: 0.5,
                          }
                        }}
                      >
                        {canEdit && (
                          <MenuItem 
                            onClick={() => onMenuEdit(g)}
                            sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--vault-navy)', '&:hover': { bgcolor: 'var(--mist)' } }}
                          >
                            Edit
                          </MenuItem>
                        )}
                        {canDelete && (
                          <MenuItem 
                            onClick={() => onMenuDelete(g)}
                            sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--critical)', '&:hover': { bgcolor: 'var(--mist)' } }}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </Menu>
                    </Box>
                  )}
                </Box>
              ))}

              {paginatedGroups.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={canEdit || canDelete ? 10 : 9} sx={{ p: 3, textAlign: 'center', fontFamily: 'Calibri', fontSize: '16px', color: 'var(--slate)' }}>
                    No groups found.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <TablePagination
          component="div"
          count={groups.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            flexShrink: 0,
            borderTop: '1px solid var(--hairline)',
            color: 'var(--slate)',
            fontFamily: 'Calibri',
            fontSize: '14px',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Calibri',
              fontSize: '14px',
              color: 'var(--slate)',
            },
            '& .MuiTablePagination-selectIcon': {
              color: 'var(--slate)',
            },
            '& .MuiIconButton-root': {
              color: 'var(--slate)',
              '&.Mui-disabled': {
                color: 'var(--hairline)',
              }
            }
          }}
        />
      </Paper>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            width: '1000px',
            maxWidth: '1000px',
            borderRadius: '16px',
            bgcolor: 'var(--canvas)',
            boxShadow: '0 8px 32px rgba(11, 37, 69, 0.15)',
            border: 'none',
            overflow: 'hidden',
            margin: { xs: '16px', md: '24px' },
            maxHeight: { xs: 'calc(100% - 32px)', md: 'calc(100% - 48px)' },
            height: { xs: 'calc(100% - 32px)', md: 'auto' }
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(11, 37, 69, 0.5)',
            backdropFilter: 'blur(5px)'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: '100%', md: '750px' }, maxHeight: { xs: '100%', md: '85vh' }, flexGrow: 1 }}>
          {/* Left Panel */}
          <Box
            sx={{
              width: { xs: '100%', md: '35%' },
              display: { xs: 'none', md: 'flex' },
              background: isDark ? 'linear-gradient(180deg, #0b1120 0%, #151C2B 100%)' : 'linear-gradient(180deg, #F4F7FB 0%, #E2EDFC 100%)',
              p: { xs: 4, md: 6 },
              '@media (max-height: 850px)': { p: 4 },
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0
            }}
          >
            {/* CSS Illustration Background (Absolute to the whole panel) */}
            <Box sx={{
              position: 'absolute',
              bottom: '-20px',
              right: '-10px',
              width: '140px',
              height: '140px',
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
              boxShadow: '0 10px 40px rgba(59, 130, 246, 0.3)',
              zIndex: 0,
              transform: 'rotate(15deg)',
              '@media (max-height: 850px)': {
                display: 'none'
              }
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: '10px',
              left: '20px',
              width: '100px',
              height: '100px',
              borderRadius: '16px',
              background: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.8)',
              zIndex: 1,
              transform: 'rotate(-10deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '@media (max-height: 850px)': {
                display: 'none'
              }
            }}>
              <GroupsIcon sx={{ fontSize: 48, color: '#3B82F6', opacity: 0.9 }} />
            </Box>

            {/* Content Container (elevated z-index) */}
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Box sx={{ width: 56, height: 56, bgcolor: '#E8F0FE', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, '@media (max-height: 850px)': { mb: 2, width: 48, height: 48 } }}>
                <GroupsIcon sx={{ color: '#1A73E8', fontSize: 32, '@media (max-height: 850px)': { fontSize: 24 } }} />
              </Box>
              <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 900, fontSize: '32px', color: isDark ? '#F8FAFC' : '#0F172A', mb: 1.5, letterSpacing: '-0.5px', '@media (max-height: 850px)': { fontSize: '24px', mb: 1 } }}>
                {isEditing ? 'Edit Group' : 'Add Group'}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '14px', color: isDark ? '#94A3B8' : '#64748B', mb: 5, lineHeight: 1.6, '@media (max-height: 850px)': { mb: 3 } }}>
                {isEditing ? 'Modify this group to manage users and update policies for controlled access.' : 'Create a new group to organize users and assign policies for controlled access.'}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, '@media (max-height: 850px)': { gap: 2.5 } }}>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <GroupsIcon sx={{ color: '#4F46E5', fontSize: 22 }} />
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 800, fontSize: '15px', color: isDark ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>Organize Users</Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: isDark ? '#94A3B8' : '#64748B' }}>Add multiple users to this group.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <SecurityIcon sx={{ color: '#059669', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 800, fontSize: '15px', color: isDark ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>Assign Policies</Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: isDark ? '#94A3B8' : '#64748B' }}>Set security and access policies.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <LockIcon sx={{ color: '#9333EA', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 800, fontSize: '15px', color: isDark ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>Control Access</Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: isDark ? '#94A3B8' : '#64748B' }}>Manage URLs and permissions.</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right Panel */}
          <Box sx={{ width: { xs: '100%', md: '65%' }, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 16, right: 16, color: 'var(--slate)', zIndex: 10 }}>
              <CloseIcon />
            </IconButton>

            <Box 
              sx={{ 
                p: { xs: 3, md: 5 }, 
                pt: { xs: 6, md: 5 }, // Add padding top on mobile to avoid overlapping with CloseIcon
                pb: 2, 
                flexGrow: 1, 
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
              }}
            >
              <Stack spacing={3}>
              <Box>
                <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1, mt: { xs: 1, md: 0 } }}>
                  Group Name <span style={{ color: 'var(--critical)' }}>*</span>
                </Typography>
                <TextField
                  size="small"
                  placeholder="e.g. Development, Marketing, HR Team"
                  name="groupNameInput"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  InputProps={{
                    startAdornment: <GroupsIcon sx={{ color: 'var(--slate)', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    ...inputStyles,
                    '& .MuiOutlinedInput-root': {
                      ...inputStyles['& .MuiOutlinedInput-root'],
                      bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF',
                    }
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>
                  Users <span style={{ color: 'var(--critical)' }}>*</span>
                </Typography>
                <Select
                  multiple
                  displayEmpty
                  name="users"
                  value={formData.users}
                  onChange={handleChange}
                  IconComponent={ExpandMoreIcon}
                  input={
                    <OutlinedInput 
                      startAdornment={<PersonOutlineIcon sx={{ color: 'var(--slate)', mr: 1, ml: 1, fontSize: 20 }} />}
                    />
                  }
                  renderValue={(selected) => {
                    if (selected.length === 0) return <Typography sx={{ color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '16px' }}>Search and select users...</Typography>;
                    return <Typography sx={{ color: 'var(--vault-navy)', fontFamily: 'Calibri', fontSize: '16px' }}>{selected.length} users selected</Typography>;
                  }}
                  open={usersMenuOpen}
                  onOpen={() => {
                    setUsersBackup(formData.users);
                    setUsersMenuOpen(true);
                  }}
                  onClose={() => setUsersMenuOpen(false)}
                  MenuProps={customDropdownMenuProps}
                  sx={{
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF',
                    width: '100%',
                    '& .MuiSelect-select': { paddingLeft: '8px !important' }
                  }}
                >
                  {availableUsers.map((u) => (
                    <MenuItem key={u._id} value={u._id} sx={{ py: 1, px: 2 }}>
                      <Checkbox 
                        checked={formData.users.includes(u._id)} 
                        size="small" 
                        sx={{ color: 'var(--slate)', mr: 1, '&.Mui-checked': { color: 'var(--edge-blue)' } }} 
                      />
                      <ListItemText 
                        primary={u.username} 
                        primaryTypographyProps={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '14px', color: 'var(--vault-navy)' }}
                      />
                    </MenuItem>
                  ))}
                  <Box onClick={(e) => e.stopPropagation()} sx={dropdownFooterStyle}>
                    <Button size="small" onClick={() => { setFormData((prev) => ({ ...prev, users: usersBackup })); setUsersMenuOpen(false); }} sx={{ color: 'var(--edge-blue)', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                    <Button size="small" variant="contained" onClick={() => setUsersMenuOpen(false)} sx={{ bgcolor: 'var(--edge-blue)', color: '#FFF', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, textTransform: 'none', boxShadow: 'none', borderRadius: '6px' }}>Done</Button>
                  </Box>
                </Select>
                {formData.users.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, p: 2, bgcolor: isDark ? 'var(--mist)' : '#FAFAFA', borderRadius: '8px', border: '1px solid var(--hairline)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {renderUsers(formData.users, users)}
                      <Typography sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                        {formData.users.length} users selected
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>
                    Department <span style={{ color: 'var(--critical)' }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Select department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <BusinessIcon sx={{ color: 'var(--slate)', mr: 1, fontSize: 20 }} />
                    }}
                    sx={{ ...inputStyles, '& .MuiOutlinedInput-root': { ...inputStyles['& .MuiOutlinedInput-root'], bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF' } }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>Location</Typography>
                  <TextField
                    size="small"
                    placeholder="Select location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <LocationOnIcon sx={{ color: 'var(--slate)', mr: 1, fontSize: 20 }} />
                    }}
                    sx={{ ...inputStyles, '& .MuiOutlinedInput-root': { ...inputStyles['& .MuiOutlinedInput-root'], bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF' } }}
                  />
                </Box>
              </Stack>

              <Box>
                <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>Description</Typography>
                <TextField
                  size="small"
                  placeholder="Describe the purpose of this group, who should be part of it, and what access it provides..."
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 500 }}
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ color: 'var(--slate)', mr: 1, mt: 1, alignSelf: 'flex-start', fontSize: 20 }} />
                  }}
                  sx={{
                    ...inputStyles,
                    '& .MuiOutlinedInput-root': {
                      ...inputStyles['& .MuiOutlinedInput-root'],
                      bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF',
                      alignItems: 'flex-start',
                      padding: '12px',
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: 0,
                    }
                  }}
                />
                <Typography sx={{ textAlign: 'right', fontFamily: 'Calibri', fontSize: '12px', color: 'var(--slate)', mt: 0.5 }}>
                  {formData.description.length}/500
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>Policies</Typography>
                  <Select
                    multiple
                    displayEmpty
                    name="enabled_policies"
                    value={formData.enabled_policies}
                    onChange={handleChange}
                    IconComponent={ExpandMoreIcon}
                    input={<OutlinedInput startAdornment={<SecurityIcon sx={{ color: 'var(--slate)', mr: 1, ml: 1, fontSize: 20 }} />} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return <Typography sx={{ color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '16px' }}>Select policies</Typography>;
                      return <Typography sx={{ color: 'var(--vault-navy)', fontFamily: 'Calibri', fontSize: '16px' }}>{selected.length} policies selected</Typography>;
                    }}
                    open={policiesMenuOpen}
                    onOpen={() => { setPoliciesBackup(formData.enabled_policies); setPoliciesMenuOpen(true); }}
                    onClose={() => setPoliciesMenuOpen(false)}
                    MenuProps={customDropdownMenuProps}
                    sx={{ ...inputStyles['& .MuiOutlinedInput-root'], bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF', width: '100%', '& .MuiSelect-select': { paddingLeft: '8px !important' } }}
                  >
                    {policies.map((p) => (
                      <MenuItem key={p._id} value={p._id} sx={{ py: 1, px: 2 }}>
                        <Checkbox 
                          checked={formData.enabled_policies.includes(p._id)} 
                          size="small" 
                          sx={{ color: 'var(--slate)', mr: 1, '&.Mui-checked': { color: 'var(--edge-blue)' } }} 
                        />
                        <ListItemText 
                          primary={p.name} 
                          primaryTypographyProps={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '14px', color: 'var(--vault-navy)' }}
                        />
                      </MenuItem>
                    ))}
                    <Box onClick={(e) => e.stopPropagation()} sx={dropdownFooterStyle}>
                      <Button size="small" onClick={() => { setFormData((prev) => ({ ...prev, enabled_policies: policiesBackup })); setPoliciesMenuOpen(false); }} sx={{ color: 'var(--edge-blue)', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={() => setPoliciesMenuOpen(false)} sx={{ bgcolor: 'var(--edge-blue)', color: '#FFF', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, textTransform: 'none', boxShadow: 'none', borderRadius: '6px' }}>Done</Button>
                    </Box>
                  </Select>
                  {formData.enabled_policies.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.enabled_policies.map(id => {
                        const pol = policies.find(x => x._id === id);
                        return (
                          <Box key={id} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'var(--blue-tint)', color: 'var(--edge-blue)', borderRadius: '6px', px: 1.5, py: 0.5, fontFamily: 'Calibri', fontSize: '13px' }}>
                            {pol ? pol.name : id}
                            <CloseIcon 
                              sx={{ ml: 1, fontSize: 14, cursor: 'pointer', '&:hover': { opacity: 0.7 } }} 
                              onClick={() => {
                                setFormData(prev => ({ ...prev, enabled_policies: prev.enabled_policies.filter(x => x !== id) }));
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>URLs</Typography>
                  <Select
                    multiple
                    displayEmpty
                    name="enabled_urls"
                    value={formData.enabled_urls}
                    onChange={handleChange}
                    IconComponent={ExpandMoreIcon}
                    input={<OutlinedInput startAdornment={<LinkIcon sx={{ color: 'var(--slate)', mr: 1, ml: 1, fontSize: 20 }} />} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return <Typography sx={{ color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '16px' }}>Add allowed or restricted URLs...</Typography>;
                      return <Typography sx={{ color: 'var(--vault-navy)', fontFamily: 'Calibri', fontSize: '16px' }}>{selected.length} URLs selected</Typography>;
                    }}
                    open={urlsMenuOpen}
                    onOpen={() => { setUrlsBackup(formData.enabled_urls); setUrlsMenuOpen(true); }}
                    onClose={() => setUrlsMenuOpen(false)}
                    MenuProps={customDropdownMenuProps}
                    sx={{ ...inputStyles['& .MuiOutlinedInput-root'], bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF', width: '100%', '& .MuiSelect-select': { paddingLeft: '8px !important' } }}
                  >
                    {urls.map((u) => (
                      <MenuItem key={u._id} value={u._id} sx={{ py: 1, px: 2 }}>
                        <Checkbox 
                          checked={formData.enabled_urls.includes(u._id)} 
                          size="small" 
                          sx={{ color: 'var(--slate)', mr: 1, '&.Mui-checked': { color: 'var(--edge-blue)' } }} 
                        />
                        <ListItemText 
                          primary={u.category_Name || u.category_name || '—'} 
                          primaryTypographyProps={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '14px', color: 'var(--vault-navy)' }}
                        />
                      </MenuItem>
                    ))}
                    <Box onClick={(e) => e.stopPropagation()} sx={dropdownFooterStyle}>
                      <Button size="small" onClick={() => { setFormData((prev) => ({ ...prev, enabled_urls: urlsBackup })); setUrlsMenuOpen(false); }} sx={{ color: 'var(--edge-blue)', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={() => setUrlsMenuOpen(false)} sx={{ bgcolor: 'var(--edge-blue)', color: '#FFF', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, textTransform: 'none', boxShadow: 'none', borderRadius: '6px' }}>Done</Button>
                    </Box>
                  </Select>
                  {formData.enabled_urls.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.enabled_urls.map(id => {
                        const urlItem = urls.find(x => x._id === id);
                        return (
                          <Box key={id} sx={{ display: 'flex', alignItems: 'center', bgcolor: 'var(--blue-tint)', color: 'var(--edge-blue)', borderRadius: '6px', px: 1.5, py: 0.5, fontFamily: 'Calibri', fontSize: '13px' }}>
                            {urlItem ? (urlItem.category_Name || urlItem.category_name) : id}
                            <CloseIcon 
                              sx={{ ml: 1, fontSize: 14, cursor: 'pointer', '&:hover': { opacity: 0.7 } }} 
                              onClick={() => {
                                setFormData(prev => ({ ...prev, enabled_urls: prev.enabled_urls.filter(x => x !== id) }));
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Stack>
              </Stack>
            </Box>

            {/* Bottom Actions */}
            <Box sx={{ p: 3, px: { xs: 3, md: 5 }, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid var(--hairline)', bgcolor: 'var(--canvas)', flexShrink: 0 }}>
              <Button 
                onClick={handleClose}
                variant="outlined"
                sx={{
                  color: 'var(--vault-navy)',
                  borderColor: 'var(--hairline)',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  height: '40px',
                  px: 4,
                  '&:hover': {
                    bgcolor: 'var(--mist)',
                    borderColor: 'var(--slate)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  bgcolor: 'var(--edge-blue)',
                  color: '#FFF',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '6px',
                  height: '40px',
                  px: 4,
                  textTransform: 'none',
                  boxShadow: 'none',
                  border: '1px solid var(--edge-blue)',
                  '&:hover': {
                    bgcolor: 'var(--edge-blue)',
                    boxShadow: '0 0 0 3px var(--blue-tint)',
                  },
                }}
              >
                {isEditing ? 'Update Group' : 'Create Group'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <CommonDeleteModal
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
        entityName={deletingGroup ? deletingGroup.name : ''}
        entityType="Group"
      />

      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Group"
        isBulk={true}
        bulkCount={selectedIds.length}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }} className="global-alert">
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}